/**
 * Clase base abstracta para la creación de sugerencias.
 * No debe ser instanciada directamente.
 * Las subclases deben implementar el método getSuggestions().
 */
class SuggestionsCreator {
  constructor() {
    if (new.target === SuggestionsCreator) {
      throw new TypeError("No puede instanciar una clase abstracta");
    }
    this.suggestions = [];
  }

  /**
   * Método abstracto que debe ser implementado por las subclases.
   * @returns {Array} Debe retornar un array de sugerencias.
   * @throws {Error} Si no es implementado por la subclase.
   */
  getSuggestions() {
    throw new Error(
      "Método getSuggestions debe ser implementado por la subclase"
    );
  }
}

/**
 * Creador de sugerencias específico para modelos relacionados a usuarios.
 * Construye sugerencias a partir de los datos de modelo y operaciones.
 *
 * @param {Object} data - Objeto con las siguientes propiedades:
 *   @param {Array} data.model - Array de modelos de usuario.
 *   @param {Array} data.ops - Array de operaciones relacionadas.
 */
class SuggestionsUsuarios extends SuggestionsCreator {
  constructor(data) {
    super();
    this.suggestions = [];
    const { model, ops } = data;
    if (!model) return;
    model.map(m => {
      Object.entries(m).forEach(([key, value]) => {
        value.map(item => {
          this.suggestions.push({
            label: item.name,
            value: item.name
          });
        });
      });
    });
    if (!ops) return;
    ops.map(op => {
      Object.entries(op).forEach(([key, value]) => {
        value.map(item => {
          this.suggestions.push({
            label: item.label,
            value: item.key
          });
        });
      });
    });
  }

  /**
   * Retorna el array de sugerencias generado.
   * @returns {Array<{label: string, value: string}>} Array de sugerencias.
   */
  getSuggestions() {
    return this.suggestions;
  }
}

/**
 * Creador de sugerencias para modelos comunes (no usuarios).
 * Construye sugerencias a partir de los datos de modelo.
 *
 * @param {Object} data - Objeto con las siguientes propiedades:
 *   @param {Array} data.model - Array de modelos.
 *   @param {Array} [data.ops] - (Opcional) Array de operaciones.
 */
class SuggestionsComunes extends SuggestionsCreator {
  constructor(data) {
    super();
    this.suggestions = [];
    const { model, ops } = data;
    if (!model) return;
    model.map(m => {
      Object.entries(m).forEach(([key, value]) => {
        value.map(item => {
          this.suggestions.push({
            label: item.name,
            value: item.name
          });
        });
      });
    });
  }

  /**
   * Retorna el array de sugerencias generado.
   * @returns {Array<{label: string, value: string}>} Array de sugerencias.
   */
  getSuggestions() {
    return this.suggestions;
  }
}

class SuggestionsRules extends SuggestionsCreator {
  constructor(data) {
    super();
    this.suggestions = [];
    const { results, rule } = data;
    if (!results) return;
    const { data: resultsData } = results;
    const rows = resultsData[rule.key];
    let keys = Object.keys(rows[0]);
    rows.map(row => {
      this.suggestions.push({
        label: row[keys[1]],
        value: row[keys[0]]
      });
    });
  }

  /**
   * Retorna el array de sugerencias generado.
   * @returns {Array<{label: string, value: string}>} Array de sugerencias.
   */
  getSuggestions() {
    return this.suggestions;
  }
}
/**
 * Clase Factory para la creación de instancias de SuggestionsCreator.
 */
export class SuggestionsFactoryMethod {
  /**
   * Crea una instancia de SuggestionsUsuarios o SuggestionsComunes según el modelo recibido.
   *
   * @param {Object} data - Objeto con las siguientes propiedades:
   *   @param {Array} data.models - Array de modelos (strings o arrays).
   *   @param {Object} data.results - Datos a pasar al constructor de la subclase.
   * @returns {SuggestionsUsuarios|SuggestionsComunes} Instancia de la subclase correspondiente.
   */
  static createSuggestions(data) {
    const { models, results, rule } = data;
    if (
      models &&
      models.length > 0 &&
      models.some(model => model.modelName.includes("Usuario"))
    ) {
      return new SuggestionsUsuarios(results);
    } else if (rule) {
      return new SuggestionsRules({ rule, results });
    } else {
      return new SuggestionsComunes(results);
    }
  }
}
